'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { characterKeys } from '../queryKeys';
import { subscriptionKeys } from '@/contexts/subscriptions';
import { createCharacter } from '../actions/createCharacter';
import { updateCharacter } from '../actions/updateCharacter';
import { deleteCharacter } from '../actions/deleteCharacter';
import { generatePortrait } from '../actions/generatePortrait';
import { generateVisualDescription } from '../actions/generateVisualDescription';
import { updateVisualDescription } from '../actions/updateVisualDescription';
import { setPrimaryPortrait } from '../actions/setPrimaryPortrait';
import { deletePortrait } from '../actions/deletePortrait';
import { analyzeCharacterPhoto } from '../actions/analyzeCharacterPhoto';
import type { CreateCharacterInput, GeneratePortraitInput } from '../types';

export function useCharacterMutations(familyId: string | null | undefined) {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: (input: CreateCharacterInput) => createCharacter(input),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: characterKeys.list(familyId) });
      }
    },
  });

  const update = useMutation({
    mutationFn: (params: { characterId: string; input: Partial<CreateCharacterInput> }) =>
      updateCharacter(params.characterId, params.input),
    onSuccess: (_result, params) => {
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(params.characterId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: characterKeys.list(familyId) });
      }
    },
  });

  const remove = useMutation({
    mutationFn: (characterId: string) => deleteCharacter(characterId),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: characterKeys.list(familyId) });
      }
    },
  });

  const portrait = useMutation({
    mutationFn: (input: GeneratePortraitInput) => generatePortrait(input),
    onSuccess: (_result, input) => {
      // Refetch detail immediately (portraitStatus='generating'), SSE event handles completion
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(input.characterId) });
    },
    onSettled: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
      }
    },
  });

  const visualDescription = useMutation({
    mutationFn: (characterId: string) => generateVisualDescription(characterId),
    onSuccess: (_result, characterId) => {
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(characterId) });
    },
    onSettled: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
      }
    },
  });

  const updateVisualDesc = useMutation({
    mutationFn: (params: { characterId: string; visualDescription: string }) =>
      updateVisualDescription(params.characterId, params.visualDescription),
    onSuccess: (_result, params) => {
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(params.characterId) });
    },
  });

  const setPrimary = useMutation({
    mutationFn: (params: { characterId: string; portraitId: string }) =>
      setPrimaryPortrait(params.characterId, params.portraitId),
    onSuccess: (_result, params) => {
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(params.characterId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: characterKeys.list(familyId) });
      }
    },
  });

  const removePortrait = useMutation({
    mutationFn: (params: { characterId: string; portraitId: string }) =>
      deletePortrait(params.characterId, params.portraitId),
    onSuccess: (_result, params) => {
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(params.characterId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: characterKeys.list(familyId) });
      }
    },
  });

  const analyzePhoto = useMutation({
    mutationFn: (params: { imageBase64: string; language?: string }) =>
      analyzeCharacterPhoto(params.imageBase64, params.language),
    onSettled: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
      }
    },
  });

  return {
    createCharacter: create,
    updateCharacter: update,
    deleteCharacter: remove,
    generatePortrait: portrait,
    generateVisualDescription: visualDescription,
    updateVisualDescription: updateVisualDesc,
    setPrimaryPortrait: setPrimary,
    deletePortrait: removePortrait,
    analyzeCharacterPhoto: analyzePhoto,
  };
}
